import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ExternalWsService } from '@core/services/features/external-ws.service';
import { ProcedureCoreService } from '@core/services/features/procedure-core.service';
import { WorkGroupsCoreService } from '@core/services/features/work-groups-core.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { createQuery, filterTruthy } from '@utils';
import { EMPTY } from 'rxjs';
import { catchError, first, map, mapTo, mergeMap, pluck, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { Role } from '../models/role.enum';
import { AuthService } from '../services/features/auth.service';
import { EnablingCoreService } from '../services/features/enabling-core.service';
import { coreActions } from './core.actions';
import { coreSelectors } from './core.selectors';

@Injectable()
export class CoreEffects {
  persistState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(coreActions.loggedIn, coreActions.loggedOut),
        withLatestFrom(this.store.select(coreSelectors.persisted)),
        tap(([action, persisted]) => localStorage.setItem('app-state', JSON.stringify(persisted)))
      ),
    { dispatch: false }
  );

  loadState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.loadState),
      map(() => coreActions.stateLoaded(JSON.parse(localStorage.getItem('app-state') || '{}')))
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.login),
      switchMap(({ username, password, returnUrl }) =>
        this.authSvc.login(username, password).pipe(
          first(),
          filterTruthy(),
          map((response) =>
            coreActions.loggedIn({ user: response.user, accessToken: response.accessToken, returnUrl })
          ),
          catchError(() => EMPTY)
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.changePassword),
      switchMap(({ username, oldPassword, newPassword }) =>
        this.authSvc.changePassword(username, oldPassword, newPassword).pipe(
          first(),
          filterTruthy(),
          map(({ user, accessToken }) =>
            coreActions.loggedIn({ user, accessToken }))
        )),
      catchError(() => EMPTY)
    )
  );

  loggedIn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(coreActions.loggedIn),
        tap(({ returnUrl, user }) => {
          if (user.role === Role.operator) {
            this.router.navigateByUrl('/app/exec');
          } else {
            if (returnUrl) {
              this.router.navigateByUrl(returnUrl);
            } else {
              this.router.navigateByUrl('/app/dashboard');
            }
          }
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() => this.actions$.pipe(ofType(coreActions.logout), mapTo(coreActions.loggedOut())));

  loggedOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(coreActions.loggedOut),
        tap(() => this.router.navigate(['/login']))
      ),
    { dispatch: false }
  );

  cacheEnablings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.cacheEnablings),
      mergeMap(() =>
        this.enablingsCoreSvc.getByQuery(createQuery({ size: 1000 })).pipe(
          first(),
          pluck('results'),
          map((enablings) => coreActions.cached({ key: 'enablings', payload: enablings })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  cacheEnablingById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.cacheEnablingById),
      mergeMap(({ id }) =>
        this.enablingsCoreSvc.getById(id).pipe(
          map((e) => coreActions.cached({ key: 'enablings', payload: [e] })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  cacheExternalWs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.cacheExternalWs),
      mergeMap(() =>
        this.externalWsSvc.getByQuery(createQuery({ size: 1000 })).pipe(
          first(),
          pluck('results'),
          map((externalWs) => coreActions.cached({ key: 'externalWs', payload: externalWs })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  cacheProcedureById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.cacheProcedureById),
      mergeMap(({ id }) =>
        this.procedureCoreSvc.getById(id).pipe(
          map((p) => coreActions.cached({ key: 'procedures', payload: [p] })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  cacheWorkGroupById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(coreActions.cacheWorkGroupById),
      mergeMap(({ id }) =>
        this.workGroupCoreSvc.get(id).pipe(
          map((p) => coreActions.cached({ key: 'workGroups', payload: [p] })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private authSvc: AuthService,
    private enablingsCoreSvc: EnablingCoreService,
    private externalWsSvc: ExternalWsService,
    private procedureCoreSvc: ProcedureCoreService,
    private workGroupCoreSvc: WorkGroupsCoreService,
    private router: Router,
    private store: Store
  ) { }
}
